import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useActiveWorld } from "../hooks/useActiveWorld";
import { 
  collection, 
  onSnapshot, 
  query, 
  where,
  getDocs,
  doc,
  updateDoc,
  arrayUnion,
  arrayRemove
} from "firebase/firestore";
import { ref, onValue } from "firebase/database";
import { database, firestore } from "../firebaseConfig";
import { completeFlowStep, stopFlowForContact, autoEvaluateCondition } from "./warmupFlowUtils";
import { deleteField } from "firebase/firestore";
import EmailPopup from "./EmailPopup";
import { logOutcomeAnalytics, logReplyAnalytics, updateLeadContext, INDUSTRY_OPTIONS, SIZE_OPTIONS, ANALYTICS_CONDITIONS } from "./analyticsTracker";
import { recordSignalCorrection } from './leadSignalEngine';
import { Letter } from 'react-letter';
import FlowSidebar from "./FlowSidebar";
import { EMAIL_ACTION_TYPES } from '../shared/flowTouchpoints';
import NotesActivityFields, { createPrivateNote, hasNoteContent, getNoteAuthor } from './NotesActivityFields';
import "./LeadSwipe.css";

const EMPTY_NOTE_DRAFT = { activity: 'call', outcome: null, sentiment: null, text: '', excludeFromAi: false };

// Favorite Tag – same structure as PREDEFINED_TAGS in FlowBuilderCanvas
const FAVORITE_TAG = { id: 'favorite', name: 'Favorite', color: '#6366f1', icon: 'fa-heart' };

// Touchpoint-Mapping für Icons und Labels
const TOUCHPOINT_MAP = {
  instagram_follow: { label: "Follow", readyLabel: "Follow", icon: "fa-instagram", isBrand: true },
  instagram_like: { label: "Post Liked", readyLabel: "Like Post", icon: "fa-instagram", isBrand: true },
  instagram_comment: { label: "Post Commented", readyLabel: "Comment on Post", icon: "fa-instagram", isBrand: true },
  instagram_dm: { label: "DM Sent", readyLabel: "Send DM", icon: "fa-instagram", isBrand: true },
  instagram_voice: { label: "Voice DM", readyLabel: "Send Voice DM", icon: "fa-instagram", isBrand: true },
  instagram_story_reply: { label: "Story Reply", readyLabel: "Reply to Story", icon: "fa-instagram", isBrand: true },
  instagram_story_mention: { label: "Story Mention", readyLabel: "Mention in Story", icon: "fa-instagram", isBrand: true },
  instagram_reel_comment: { label: "Reel Comment", readyLabel: "Comment on Reel", icon: "fa-instagram", isBrand: true },
  linkedin_connect: { label: "Connection Request", readyLabel: "Send Connection Request", icon: "fa-linkedin", isBrand: true },
  linkedin_connected: { label: "Connected", icon: "fa-linkedin", isBrand: true },
  linkedin_message: { label: "Message Sent", readyLabel: "Send Message", icon: "fa-linkedin", isBrand: true },
  linkedin_like: { label: "Post Liked", readyLabel: "Like Post", icon: "fa-linkedin", isBrand: true },
  linkedin_comment: { label: "Post Commented", readyLabel: "Comment on Post", icon: "fa-linkedin", isBrand: true },
  linkedin_follow: { label: "Followed", readyLabel: "Follow", icon: "fa-linkedin", isBrand: true },
  linkedin_voice: { label: "Voice Message", readyLabel: "Send Voice Message", icon: "fa-linkedin", isBrand: true },
  whatsapp_message: { label: "Message Sent", readyLabel: "Send Message", icon: "fa-whatsapp", isBrand: true },
  whatsapp_voice: { label: "Voice Message", readyLabel: "Send Voice Message", icon: "fa-whatsapp", isBrand: true },
  whatsapp_call: { label: "WhatsApp Call", readyLabel: "Make Call", icon: "fa-whatsapp", isBrand: true },
  email_sent: { label: "Email Sent", readyLabel: "Send Email", icon: "fa-paper-plane", isBrand: false },
  cold_email: { label: "Cold Email", readyLabel: "Send Cold Email", icon: "fa-snowflake", isBrand: false },
  follow_up_email: { label: "Follow Up", readyLabel: "Send Follow Up", icon: "fa-reply", isBrand: false },
  phone_call: { label: "Phone Call", readyLabel: "Make Call", icon: "fa-phone-alt", isBrand: false },
  condition: { label: "Decision", readyLabel: "Decision Required", icon: "fa-question", isBrand: false },
};

const socialPlatforms = [
  { key: 'linkedin',  icon: 'fab fa-linkedin',    label: 'LinkedIn',    color: '#0077b5', cls: 'li' },
  { key: 'facebook',  icon: 'fab fa-facebook',    label: 'Facebook',    color: '#1877f2', cls: 'fb' },
  { key: 'instagram', icon: 'fab fa-instagram',   label: 'Instagram',   color: '#e4405f', cls: 'ig' },
  { key: 'x',         icon: 'fab fa-x-twitter',   label: 'X / Twitter', color: '#000000', cls: 'x' },
  { key: 'tiktok',    icon: 'fab fa-tiktok',      label: 'TikTok',      color: '#010101', cls: 'tt' },
  { key: 'youtube',   icon: 'fab fa-youtube',     label: 'YouTube',     color: '#ff0000', cls: 'yt' },
  { key: 'xing',      icon: 'fab fa-xing',        label: 'Xing',        color: '#006567', cls: 'xing' },
  { key: 'website',   icon: 'fa fa-globe',        label: 'Website',     color: '#6366f1', cls: 'web' },
];

const parseSocials = (row) => {
  let socials = {};
  try {
    if (row.lead_socials && typeof row.lead_socials === 'string') socials = JSON.parse(row.lead_socials);
    else if (row.lead_socials && typeof row.lead_socials === 'object') socials = row.lead_socials;
  } catch (e) { /* ignore */ }
  const fallbacks = {
    linkedin: row.lead_linkedin,
    instagram: row.lead_instagram || row.lead_instagram_username,
    facebook: row.lead_facebook,
    tiktok: row.lead_tiktok,
    x: row.lead_twitter,
    youtube: row.lead_youtube,
    xing: row.lead_xing,
    website: row.lead_website,
  };
  for (const [key, val] of Object.entries(fallbacks)) {
    if (val && !socials[key]) socials[key] = val;
  }
  return socials;
};

const buildSocialHref = (key, value) => {
  if (!value || !value.trim()) return null;
  const v = value.trim();
  if (v.startsWith('http')) return v;
  switch (key) {
    case 'linkedin': return `https://linkedin.com/in/${v}`;
    case 'facebook': return `https://facebook.com/${v}`;
    case 'instagram': return `https://instagram.com/${v.replace('@', '')}`;
    case 'x': return `https://x.com/${v.replace('@', '')}`;
    case 'tiktok': return `https://tiktok.com/@${v.replace('@', '')}`;
    case 'youtube': return `https://youtube.com/@${v.replace('@', '')}`;
    case 'xing': return `https://xing.com/profile/${v}`;
    case 'website': return `https://${v}`;
    default: return v;
  }
};

const getTouchpointInfo = (stepType, step = null) => {
  if (step) {
    if (step.nodeType === 'delay') {
      const days = parseInt(step.delayDays) || 0;
      return { label: `Wait ${days} day${days !== 1 ? 's' : ''}`, readyLabel: `Wait ${days} day${days !== 1 ? 's' : ''}`, icon: "fa-hourglass-half", isBrand: false };
    }
    if (step.nodeType === 'tag') return { label: "Add Tags", readyLabel: "Add Tags", icon: "fa-tags", isBrand: false };
    if (step.nodeType === 'end') {
      const endType = step.endType || 'completed';
      return { label: endType === 'disqualified' ? "Disqualified" : "Flow Completed", readyLabel: endType === 'disqualified' ? "Disqualified" : "Flow Completed", icon: endType === 'disqualified' ? "fa-ban" : "fa-check-circle", isBrand: false };
    }
    if (step.nodeType === 'condition') return { label: step.customQuestion || "Decision", readyLabel: step.customQuestion || "Decision Required", icon: "fa-question", isBrand: false };
    if (step.nodeType === 'auto_send_email') return { label: "Auto Send Email", readyLabel: "Sending Email...", icon: "fa-paper-plane", isBrand: false, isAutomated: true };
    if (step.nodeType === 'auto_check_reply') return { label: "Checking Reply", readyLabel: "Waiting for Reply...", icon: "fa-envelope-open-text", isBrand: false, isAutomated: true };
    if (step.nodeType === 'log_notes') return { label: "Log Notes", readyLabel: "Log Notes", icon: "fa-clipboard-list", isBrand: false };
  }
  return TOUCHPOINT_MAP[stepType] || { label: stepType || "Action", readyLabel: "Complete Action", icon: "fa-tasks", isBrand: false };
};

// Determine which social platform a flow belongs to, based on its action steps.
// Returns 'instagram', 'linkedin' or null. The first matching step wins – flows
// are single-platform in practice (we never run Instagram + LinkedIn together).
const getFlowPlatform = (flow) => {
  if (!flow?.steps) return null;
  for (const step of flow.steps) {
    const stepType = step?.type || "";
    if (stepType.startsWith("linkedin")) return "linkedin";
    if (stepType.startsWith("instagram")) return "instagram";
  }
  return null;
};

const findNextStepFromEdges = (flow, currentStep, decision = null) => {
  if (!flow.visualData?.edges || !currentStep?.id) return null;
  const outgoingEdges = flow.visualData.edges.filter(edge => edge.source === currentStep.id);
  if (outgoingEdges.length === 0) return null;
  let targetEdge = outgoingEdges[0];
  if (currentStep.nodeType === 'condition') {
    const handle = decision || 'yes';
    const matchEdge = outgoingEdges.find(e => e.sourceHandle === handle);
    if (matchEdge) targetEdge = matchEdge;
  }
  return flow.steps.find(step => step.id === targetEdge.target) || null;
};

const doesPathLeadToEnd = (flow, fromStep, decision) => {
  let step = findNextStepFromEdges(flow, fromStep, decision);
  const visited = new Set();
  let depth = 0;
  while (step && depth < 15) {
    if (visited.has(step.id)) break;
    visited.add(step.id);
    if (step.nodeType === 'end' || step.type === 'end') {
      const endType = step.endType || 'completed';
      return { isEnd: true, endType, isDisqualified: ['disqualified', 'not_interested', 'wrong_contact'].includes(endType) };
    }
    const isNonInteractive = step.nodeType === 'tag' || (step.nodeType === 'delay' && (parseInt(step.delayDays) || 0) === 0);
    if (!isNonInteractive) break;
    step = findNextStepFromEdges(flow, step);
    depth++;
  }
  return { isEnd: false, endType: null, isDisqualified: false };
};

const HIDDEN_TIMELINE_TYPES = new Set(['tag']);

const buildTimelinePath = (flow, stepHistory, currentStepIndex) => {
  if (!flow?.steps) return [];
  const path = [];

  const completed = Object.entries(stepHistory || {})
    .map(([idx, data]) => ({ stepIndex: Number(idx), ...data }))
    .sort((a, b) => (a.completedAt || 0) - (b.completedAt || 0));

  for (const entry of completed) {
    const step = flow.steps[entry.stepIndex];
    if (!step || HIDDEN_TIMELINE_TYPES.has(step.nodeType)) continue;
    if (entry.stepIndex === currentStepIndex) continue;
    path.push({ step, stepIndex: entry.stepIndex, status: 'done', decision: entry.decision, autoCompleted: entry.autoCompleted });
  }

  const currentStep = flow.steps[currentStepIndex];
  if (currentStep && !HIDDEN_TIMELINE_TYPES.has(currentStep.nodeType)) {
    path.push({ step: currentStep, stepIndex: currentStepIndex, status: 'current' });
  }

  let traceStep = currentStep;
  let count = 0;
  const seenIds = new Set(path.map(p => p.step?.id));
  while (traceStep && count < 6) {
    const next = findNextStepFromEdges(flow, traceStep, traceStep.nodeType === 'condition' ? 'yes' : null);
    if (!next || seenIds.has(next.id)) break;
    seenIds.add(next.id);
    if (!HIDDEN_TIMELINE_TYPES.has(next.nodeType)) {
      path.push({ step: next, stepIndex: flow.steps.indexOf(next), status: 'upcoming' });
    }
    if (next.nodeType === 'end') break;
    traceStep = next;
    count++;
  }

  return path;
};

/**
 * LeadSwipe – Premium card-based flow execution
 */
const LeadSwipe = () => {
  const navigate = useNavigate();
  const { userId: activeWorldUserId, isLoading: teamLoading, readOnly, canWrite, hasFeature } = useActiveWorld();
  const userId = activeWorldUserId;
  const isWriteAllowed = canWrite();

  const [contacts, setContacts] = useState([]);
  const [warmupFlows, setWarmupFlows] = useState([]);
  const [flowsLoaded, setFlowsLoaded] = useState(false);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [completingStep, setCompletingStep] = useState({});
  const [filterType, setFilterType] = useState("ready");
  // Platform path: we run either Instagram OR LinkedIn outreach, never both at
  // once. The switcher toggles between the two paths; the choice is persisted.
  const [platformFilter, setPlatformFilter] = useState(() => {
    try {
      const saved = localStorage.getItem("salesgodSwipePlatform");
      if (saved === "instagram" || saved === "linkedin") return saved;
    } catch (e) {}
    return "instagram";
  });

  useEffect(() => {
    try { localStorage.setItem("salesgodSwipePlatform", platformFilter); } catch (e) {}
  }, [platformFilter]);

  // Flow Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarStepFilter, setSidebarStepFilter] = useState(null);

  // Removed-contacts mode
  const [removedMode, setRemovedMode] = useState(false);
  const [removedContacts, setRemovedContacts] = useState([]);
  const [removedLoading, setRemovedLoading] = useState(false);
  const removedFlowIdRef = useRef(null);

  // Live timer tick (every second for countdown)
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 1000);
    return () => {
      clearInterval(interval);
      if (toastTimeout.current) clearTimeout(toastTimeout.current);
      if (favToastTimeout.current) clearTimeout(favToastTimeout.current);
    };
  }, []);

  // Toast
  const [toast, setToast] = useState(null);
  const toastTimeout = useRef(null);
  const showToast = useCallback((message, type = 'success') => {
    if (toastTimeout.current) clearTimeout(toastTimeout.current);
    setToast({ message, type });
    toastTimeout.current = setTimeout(() => setToast(null), 2500);
  }, []);

  // Favorite toast (bottom-right with heart animation)
  const [favToast, setFavToast] = useState(null);
  const favToastTimeout = useRef(null);
  const showFavToast = useCallback((type = 'added') => {
    if (favToastTimeout.current) clearTimeout(favToastTimeout.current);
    setFavToast({ type, key: Date.now() });
    favToastTimeout.current = setTimeout(() => setFavToast(null), 2200);
  }, []);

  // Confirm dialog for dangerous decisions
  const [confirmDialog, setConfirmDialog] = useState(null);

  // Email Popup
  const [emailPopupOpen, setEmailPopupOpen] = useState(false);
  const [emailPopupRowData, setEmailPopupRowData] = useState(null);

  // Data Capture (for data_capture flow nodes)
  const [captureData, setCaptureData] = useState({});
  // Composer state for the flow "Log Notes" step (replica of the Notes composer).
  const [noteDraft, setNoteDraft] = useState(EMPTY_NOTE_DRAFT);

  // Notes state
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noteType, setNoteType] = useState("private");
  const [noteSaving, setNoteSaving] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [editingType, setEditingType] = useState("private");
  const noteInputRef = useRef(null);
  const editInputRef = useRef(null);

  // Bio expanded state
  const [bioExpanded, setBioExpanded] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Email History state
  const [contactEmails, setContactEmails] = useState({});
  const [loadingEmails, setLoadingEmails] = useState({});
  const [emailHistoryOpen, setEmailHistoryOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  // Tinder state
  const [currentIndex, setCurrentIndex] = useState(0);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const cardRef = useRef(null);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const isHorizontalSwipe = useRef(null);

  // Load warmup flows
  useEffect(() => {
    if (!userId) return;
    const flowsRef = ref(database, `World/${userId}/settings/warmupFlows`);
    const unsubscribe = onValue(flowsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setWarmupFlows(Object.keys(data).map((key) => ({ id: key, ...data[key] })));
      } else {
        setWarmupFlows([]);
      }
      setFlowsLoaded(true);
    }, () => setFlowsLoaded(true));
    return () => unsubscribe();
  }, [userId]);

  // Realtime contacts
  useEffect(() => {
    if (!userId || !flowsLoaded) return;
    setContactsLoading(true);
    const q = query(collection(firestore, `World/${userId}/db`), where("warmupFlow", "!=", null));
    const unsub = onSnapshot(q, (snapshot) => {
      const active = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(c => c.warmupFlow && c.warmupFlow.currentStepIndex >= 0 && !c.flowEndStatus && !c.isDisqualified);
      setContacts(active);
      setContactsLoading(false);
    }, () => { setContacts([]); setContactsLoading(false); });
    return () => unsub();
  }, [userId, flowsLoaded]);

  const getFlowForContact = (contact) => warmupFlows.find(f => f.id === contact.warmupFlow?.flowId);
  const isStepReady = (contact) => !contact.warmupFlow?.nextStepAvailableAt || Date.now() >= contact.warmupFlow.nextStepAvailableAt;
  const getTimeRemaining = (contact) => Math.max(0, (contact.warmupFlow?.nextStepAvailableAt || 0) - Date.now());

  const formatTimeRemaining = (ms) => {
    if (ms <= 0) return "Ready";
    const totalSeconds = Math.floor(ms / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m ${seconds}s`;
    }
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  };

  // Format time with separate numbers and labels for elegant display
  const formatTimeRemainingDetailed = (ms) => {
    if (ms <= 0) return null;
    const totalSeconds = Math.floor(ms / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    const parts = [];
    if (days > 0) parts.push({ value: days, label: 'Days' });
    if (hours > 0 || days > 0) parts.push({ value: hours, label: 'Hours' });
    if (minutes > 0 || hours > 0 || days > 0) parts.push({ value: minutes, label: 'Min' });
    parts.push({ value: seconds, label: 'Sec' });
    
    return parts;
  };

  const getFlowStatus = (contact) => {
    const flow = getFlowForContact(contact);
    if (!flow || !contact.warmupFlow) return null;
    const stepIndex = contact.warmupFlow.currentStepIndex;
    const currentStep = flow.steps[stepIndex];
    if (!currentStep) return null;
    const touchpoint = getTouchpointInfo(currentStep.type, currentStep);
    const ready = isStepReady(contact);
    const timeRemaining = getTimeRemaining(contact);
    // Manual conditions that require user decision (Yes/No buttons)
    const manualConditionTypes = ['shown_interest', 'qualified', 'decision_maker', 'has_budget', 'good_fit', 'ready_to_buy', 'needs_follow_up', 'meeting_went_well', 'responded_positively', 'custom_question', 'meeting_booked', 'deal_closed'];
    const isManualCondition = currentStep.nodeType === 'condition' && manualConditionTypes.includes(currentStep.condition);
    const isDataCapture = currentStep.nodeType === 'data_capture';
    const isLogNotes = currentStep.nodeType === 'log_notes';
    // Auto-evaluable conditions (system can resolve without user input)
    const isAutoEvaluable = currentStep.nodeType === 'condition' && !isManualCondition &&
      autoEvaluateCondition(currentStep.condition, contact) !== null;
    const CONDITION_DISPLAY = {
      meeting_booked: 'Meeting booked?', deal_closed: 'Deal closed?',
      is_favorite: 'Is Favorite?',
      shown_interest: 'Has shown interest?', qualified: 'Qualified?',
      decision_maker: 'Decision maker?', has_budget: 'Has budget?',
      good_fit: 'Good fit?', ready_to_buy: 'Ready to buy?',
      needs_follow_up: 'Needs follow up?', meeting_went_well: 'Meeting went well?',
      responded_positively: 'Responded positively?',
    };
    const conditionLabel = currentStep.customQuestion || CONDITION_DISPLAY[currentStep.condition] || "Continue?";
    const isAnalyticsCondition = !!ANALYTICS_CONDITIONS[currentStep.condition];
    return { flow, stepIndex, currentStep, touchpoint, ready, timeRemaining, isManualCondition, isDataCapture, isLogNotes, isAutoEvaluable, conditionLabel, isAnalyticsCondition };
  };

  const getContactName = (contact) => {
    if (contact.business_name) return contact.business_name;
    try {
      if (contact.lead_decision_makers) {
        const dms = typeof contact.lead_decision_makers === 'string' ? JSON.parse(contact.lead_decision_makers) : contact.lead_decision_makers;
        if (dms.length > 0 && dms[0].name) return dms[0].name;
      }
    } catch (e) {}
    if (contact.lead_firstname) return `${contact.lead_firstname} ${contact.lead_lastname || ''}`.trim();
    if (contact.lead_name) return contact.lead_name;
    if (contact.lead_email) return contact.lead_email.split("@")[0];
    return "Unknown";
  };

  const handleCompleteStep = async (contactId, stepIndex, decision = null) => {
    if (!isWriteAllowed) { showToast('Read-only access', 'error'); return { success: false }; }
    const contact = contacts.find(c => c.id === contactId);
    if (!contact) return { success: false };
    const flow = getFlowForContact(contact);
    if (!flow) return { success: false };
    try {
      const result = await completeFlowStep(userId, contactId, flow, stepIndex, "warmup", decision);
      return result;
    } catch (error) {
      console.error("Error completing step:", error);
      return { success: false };
    }
  };

  const handleStopFlow = async (contactId) => {
    if (!isWriteAllowed) { showToast('Read-only access', 'error'); return; }
    const contact = contacts.find(c => c.id === contactId);
    if (!contact) return;
    const flow = getFlowForContact(contact);
    try {
      const success = await stopFlowForContact(userId, contactId, flow, "warmup");
      if (success) showToast(`Flow stopped – ${getContactName(contact)}`, 'info');
    } catch (error) {
      showToast('Error stopping flow', 'error');
    }
  };

  // Email steps are only actionable when the email platform filter is active.
  // Otherwise they appear as "waiting" (not the user's job in Swipe mode).
  const EMAIL_STEP_TYPES_ALL = EMAIL_ACTION_TYPES;

  const isContactEmailSuppressed = (contact) => {
    if (platformFilter === "email") return false; // email filter active → show normally
    const flow = getFlowForContact(contact);
    if (!flow || !contact.warmupFlow) return false;
    const currentStep = flow.steps[contact.warmupFlow.currentStepIndex];
    if (!currentStep) return false;
    return EMAIL_STEP_TYPES_ALL.includes(currentStep.type || "");
  };

  // Effective readiness: technically ready AND not an email step while outside email filter
  const isEffectivelyReady = (contact) => isStepReady(contact) && !isContactEmailSuppressed(contact);

  // Platform path matching – a contact belongs to the active path (Instagram or
  // LinkedIn) when its flow contains steps of that platform.
  const matchesPlatformFilter = (contact) => {
    if (platformFilter === "all") return true;
    const flow = getFlowForContact(contact);
    if (!flow || !contact.warmupFlow) return false;
    if (platformFilter === "instagram" || platformFilter === "linkedin") {
      return getFlowPlatform(flow) === platformFilter;
    }
    if (platformFilter === "email") {
      const currentStep = flow.steps[contact.warmupFlow.currentStepIndex];
      return !!currentStep && EMAIL_STEP_TYPES_ALL.includes(currentStep.type || "");
    }
    return true;
  };

  // Auto-cleanup orphaned contacts (flow deleted but contact still references it).
  // Uses a Set ref so each orphan is cleaned exactly once, no matter how often the effect re-runs.
  const cleanedOrphansRef = useRef(new Set());
  useEffect(() => {
    if (!userId || !flowsLoaded || !isWriteAllowed) return;
    for (const c of contacts) {
      if (c.warmupFlow?.flowId && !getFlowForContact(c) && !cleanedOrphansRef.current.has(c.id)) {
        cleanedOrphansRef.current.add(c.id);
        stopFlowForContact(userId, c.id, null, "warmup").catch(() => {});
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts, warmupFlows, userId, flowsLoaded, isWriteAllowed]);

  // Auto-advance elapsed delay steps. Delays are non-interactive — when their
  // timer expires the system should silently advance to the next flow step
  // instead of cluttering the "ready" filter with non-actionable cards.
  const autoAdvancingDelaysRef = useRef(new Set());
  useEffect(() => {
    if (!userId || !flowsLoaded || !isWriteAllowed) return;
    for (const contact of contacts) {
      if (autoAdvancingDelaysRef.current.has(contact.id)) continue;
      const flow = getFlowForContact(contact);
      if (!flow || !contact.warmupFlow) continue;
      const step = flow.steps[contact.warmupFlow.currentStepIndex];
      if (!step || step.nodeType !== 'delay') continue;
      if (!isStepReady(contact)) continue;
      autoAdvancingDelaysRef.current.add(contact.id);
      completeFlowStep(userId, contact.id, flow, contact.warmupFlow.currentStepIndex, "warmup")
        .catch(() => {})
        .finally(() => autoAdvancingDelaysRef.current.delete(contact.id));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts, warmupFlows, userId, flowsLoaded, isWriteAllowed]);

  // Filter & Sort (recomputed each tick so time-based ready/waiting transitions are immediate)
  const filteredContacts = contacts.filter(contact => {
    if (contact.warmupFlow?.flowId && !getFlowForContact(contact)) return false;
    // Sidebar step filter: restrict to specific flow + step (bypasses ready/waiting header filter)
    if (sidebarStepFilter) {
      if (contact.warmupFlow?.flowId !== sidebarStepFilter.flowId) return false;
      if (contact.warmupFlow?.currentStepIndex !== sidebarStepFilter.stepIndex) return false;
      return true;
    }
    if (filterType === "favorites" && !(contact.tags && contact.tags[FAVORITE_TAG.id])) return false;
    if (filterType === "ready" && !isEffectivelyReady(contact)) return false;
    if (filterType === "waiting" && isEffectivelyReady(contact)) return false;
    if (!matchesPlatformFilter(contact)) return false;
    return true;
  });

  const sortedContacts = [...filteredContacts].sort((a, b) => {
    const aReady = isEffectivelyReady(a);
    const bReady = isEffectivelyReady(b);
    if (aReady && !bReady) return -1;
    if (!aReady && bReady) return 1;
    const timeDiff = getTimeRemaining(a) - getTimeRemaining(b);
    if (timeDiff !== 0) return timeDiff;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });

  // Always-current ref for use inside async callbacks / timeouts (avoids stale closures)
  const sortedContactsRef = useRef([]);
  sortedContactsRef.current = sortedContacts;

  // Track current contact
  const currentContactId = useRef(null);
  useEffect(() => {
    if (sortedContacts[currentIndex]) currentContactId.current = sortedContacts[currentIndex].id;
  }, [currentIndex, sortedContacts.length]);

  useEffect(() => { setCurrentIndex(0); currentContactId.current = null; }, [filterType, platformFilter, sidebarStepFilter]);

  useEffect(() => {
    if (!currentContactId.current || sortedContacts.length === 0) {
      if (sortedContacts.length > 0) setCurrentIndex(prev => Math.min(prev, sortedContacts.length - 1));
      return;
    }
    const idx = sortedContacts.findIndex(c => c.id === currentContactId.current);
    if (idx >= 0) {
      // Only follow the tracked contact if it's still ready (actionable).
      // If it became "waiting" (e.g. after condition → delay step), stop tracking
      // and keep the current position so the user sees the next ready contact.
      const trackedContact = sortedContacts[idx];
      if (isStepReady(trackedContact)) {
        setCurrentIndex(idx);
      } else {
        currentContactId.current = null;
        setCurrentIndex(prev => Math.min(prev, sortedContacts.length - 1));
      }
    } else {
      currentContactId.current = null;
      setCurrentIndex(prev => Math.min(prev, Math.max(0, sortedContacts.length - 1)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts]);

  // Load removed contacts for a given flow and switch to removed mode
  const handleShowRemoved = useCallback(async (flowId) => {
    if (!userId || !flowId) return;
    setRemovedLoading(true);
    setRemovedMode(true);
    setCurrentIndex(0);
    removedFlowIdRef.current = flowId;
    try {
      const tagField = `tags.flow_completed_${flowId}`;
      const q = query(
        collection(firestore, `World/${userId}/db`),
        where(tagField, "!=", null)
      );
      const snap = await getDocs(q);
      const results = snap.docs.map((d) => {
        const data = d.data();
        const tag = data.tags?.[`flow_completed_${flowId}`];
        return {
          id: d.id,
          ...data,
          _endType: tag?.endType || data.flowEndStatus || "completed",
          _endedAt: tag?.addedAt || data.flowEndedAt || null,
        };
      });
      results.sort((a, b) => {
        const ta = a._endedAt ? new Date(a._endedAt).getTime() : 0;
        const tb = b._endedAt ? new Date(b._endedAt).getTime() : 0;
        return tb - ta;
      });
      setRemovedContacts(results);
    } catch (err) {
      console.error("Error loading removed contacts:", err);
      setRemovedContacts([]);
    }
    setRemovedLoading(false);
  }, [userId]);

  const exitRemovedMode = useCallback(() => {
    setRemovedMode(false);
    setRemovedContacts([]);
    removedFlowIdRef.current = null;
    setCurrentIndex(0);
  }, []);

  // Navigation – always bounds-safe, guarded against rapid taps
  const isAnimating = useRef(false);
  const navTimerRef = useRef(null);

  const goNext = useCallback(() => {
    if (isAnimating.current) return;
    setCurrentIndex(prev => {
      const list = sortedContactsRef.current;
      const maxIdx = list.length - 1;
      if (prev < maxIdx) {
        isAnimating.current = true;
        setSwipeDirection('exit-left');
        navTimerRef.current = setTimeout(() => {
          const next = prev + 1;
          if (list[next]) currentContactId.current = list[next].id;
          setSwipeDirection('enter-right');
          setCurrentIndex(next);
          requestAnimationFrame(() => {
            setTimeout(() => { setSwipeDirection(null); isAnimating.current = false; }, 20);
          });
        }, 250);
        return prev;
      }
      return prev;
    });
  }, []);

  const goPrev = useCallback(() => {
    if (isAnimating.current) return;
    setCurrentIndex(prev => {
      if (prev > 0) {
        isAnimating.current = true;
        setSwipeDirection('exit-right');
        navTimerRef.current = setTimeout(() => {
          const list = sortedContactsRef.current;
          const next = prev - 1;
          if (list[next]) currentContactId.current = list[next].id;
          setSwipeDirection('enter-left');
          setCurrentIndex(next);
          requestAnimationFrame(() => {
            setTimeout(() => { setSwipeDirection(null); isAnimating.current = false; }, 20);
          });
        }, 250);
        return prev;
      }
      return prev;
    });
  }, []);

  useEffect(() => {
    return () => { if (navTimerRef.current) clearTimeout(navTimerRef.current); };
  }, []);

  const handleActionAndAdvance = useCallback(async (contactId, stepIndex, decision = null) => {
    const contact = contacts.find(c => c.id === contactId);
    if (!contact) return;
    const flow = getFlowForContact(contact);
    if (!flow) return;

    const currentStep = flow.steps[stepIndex];
    const isCondition = currentStep?.nodeType === 'condition';
    const name = getContactName(contact);

    // Pre-compute what comes after this step (for conditions: based on the actual decision)
    let nextStepLabel = null;
    if (isCondition && decision) {
      const nextStep = findNextStepFromEdges(flow, currentStep, decision);
      if (nextStep && nextStep.nodeType !== 'end' && nextStep.type !== 'end') {
        const tp = getTouchpointInfo(nextStep.type, nextStep);
        nextStepLabel = nextStep.nodeType === 'condition'
          ? (nextStep.customQuestion || 'Decision')
          : (tp.readyLabel || tp.label);
      }
    }

    // Always stop tracking BEFORE the Firestore write.
    // For conditions: the contact stays in the list at the same stable-sorted position,
    // so no tracking is needed – the card updates in-place via onSnapshot.
    // For regular actions: we advance manually right after the write completes.
    currentContactId.current = null;

    setCompletingStep(prev => ({ ...prev, [contactId]: decision || 'action' }));
    const result = await handleCompleteStep(contactId, stepIndex, decision);
    setCompletingStep(prev => ({ ...prev, [contactId]: null }));

    if (!result.success) {
      showToast('Something went wrong', 'error');
      return;
    }

    if (isCondition) {
      // ─── CONDITION: STAY on this contact ───
      // The card updates in-place with the new step via onSnapshot (stable sort keeps position).
      if (nextStepLabel) {
        showToast(`${decision === 'yes' ? '✓ Yes' : '✗ No'} → ${nextStepLabel}`, 'success');
      } else {
        showToast(`${decision === 'yes' ? 'Yes' : 'No'} – ${name}`, 'success');
      }

      // Fire-and-forget: log analytics outcome for analytics-aware conditions
      try {
        const cond = currentStep?.condition;
        if (cond === 'meeting_booked' && decision === 'yes') {
          logOutcomeAnalytics(userId, contactId, { meeting: true });
        } else if (cond === 'deal_closed' && decision === 'yes') {
          logOutcomeAnalytics(userId, contactId, {
            deal: true,
            dealValue: contact.lead_deal_value ? Number(contact.lead_deal_value) : undefined,
          });
        } else if (cond === 'responded_positively') {
          logReplyAnalytics(userId, contactId, {
            replyType: decision === 'yes' ? 'positive' : 'objection',
            enthusiasmScore: contact.lead_happyness || null,
            replySubtype: contact.lead_reply_subtype || null,
          });
        }
      } catch (_) {}

      if (result.isComplete) {
        showToast(`✓ Flow completed – ${name}`, 'success');
        setSwipeDirection('exit-left');
        setTimeout(() => setSwipeDirection(null), 300);
      }
      return;
    }

    // ─── REGULAR ACTION (Done): immediately advance to the NEXT contact ───
    showToast(result.isComplete ? `✓ Flow completed – ${name}` : `✓ Done – ${name}`, 'success');

    // Phase 1: current card exits to the left
    setSwipeDirection('exit-left');
    setTimeout(() => {
      // Phase 2: determine next contact using the LIVE list (sortedContactsRef avoids stale closure)
      const liveList = sortedContactsRef.current;
      const doneIdx = liveList.findIndex(c => c.id === contactId);

      if (doneIdx >= 0 && doneIdx < liveList.length - 1) {
        // Contact still in list and there is a next one → advance forward
        const nextContact = liveList[doneIdx + 1];
        currentContactId.current = nextContact.id; // track next so useEffect follows if list shifts
        setCurrentIndex(doneIdx + 1);
      } else {
        // Either contact was already removed by onSnapshot, or it was the last one.
        // The [contacts] useEffect has already (or will) position us correctly.
        currentContactId.current = null;
      }

      // Phase 3: new card enters from the right
      setSwipeDirection('enter-right');
      requestAnimationFrame(() => {
        setTimeout(() => setSwipeDirection(null), 20);
      });
    }, 250);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts, warmupFlows, userId]);

  const handleSafeNoDecision = useCallback((contactId, stepIndex) => {
    const contact = contacts.find(c => c.id === contactId);
    if (!contact) return;
    const flow = getFlowForContact(contact);
    if (!flow) return;
    const step = flow.steps[stepIndex];
    if (!step) return;
    const pathResult = doesPathLeadToEnd(flow, step, 'no');
    if (pathResult.isEnd) {
      setConfirmDialog({
        contactId,
        stepIndex,
        contactName: getContactName(contact),
        isDisqualified: pathResult.isDisqualified,
        endType: pathResult.endType,
      });
    } else {
      handleActionAndAdvance(contactId, stepIndex, 'no');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts, warmupFlows, handleActionAndAdvance]);

  // Touch/drag
  const handlePointerDown = (e) => { startXRef.current = e.clientX; startYRef.current = e.clientY; isHorizontalSwipe.current = null; setIsDragging(true); setDragX(0); };
  const handlePointerMove = (e) => {
    if (!isDragging) return;
    const diffX = e.clientX - startXRef.current;
    const diffY = e.clientY - startYRef.current;
    if (isHorizontalSwipe.current === null && (Math.abs(diffX) > 10 || Math.abs(diffY) > 10)) {
      isHorizontalSwipe.current = Math.abs(diffX) > Math.abs(diffY);
    }
    if (isHorizontalSwipe.current) { e.preventDefault(); setDragX(diffX); }
  };
  const handlePointerUp = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (dragX < -100) goNext();
    else if (dragX > 100) goPrev();
    setDragX(0);
    isHorizontalSwipe.current = null;
  };

  // Keyboard
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        if (!isWriteAllowed) return;
        const contact = sortedContacts[currentIndex];
        if (!contact) return;
        const status = getFlowStatus(contact);
        const effReady = status?.ready && !isContactEmailSuppressed(contact);
        if (effReady && !status.isManualCondition && !completingStep[contact.id]) handleActionAndAdvance(contact.id, status.stepIndex);
      }
      if (e.key === 'y' || e.key === 'Y') {
        if (!isWriteAllowed) return;
        const contact = sortedContacts[currentIndex];
        if (!contact) return;
        const status = getFlowStatus(contact);
        const effReady = status?.ready && !isContactEmailSuppressed(contact);
        if (effReady && status.isManualCondition && !completingStep[contact.id]) handleActionAndAdvance(contact.id, status.stepIndex, 'yes');
      }
      if (e.key === 'n' || e.key === 'N') {
        if (!isWriteAllowed) return;
        const contact = sortedContacts[currentIndex];
        if (!contact) return;
        const status = getFlowStatus(contact);
        const effReady = status?.ready && !isContactEmailSuppressed(contact);
        if (effReady && status.isManualCondition && !completingStep[contact.id]) handleSafeNoDecision(contact.id, status.stepIndex);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goNext, goPrev, currentIndex, sortedContacts, completingStep, handleActionAndAdvance, handleSafeNoDecision, isWriteAllowed]);

  // Auto-resolve auto-evaluable conditions (e.g. is_favorite, has_phone, has_email, has_company)
  useEffect(() => {
    const contact = sortedContacts[currentIndex];
    if (!contact) return;
    const status = getFlowStatus(contact);
    if (status?.ready && status.isAutoEvaluable && !completingStep[contact.id]) {
      const autoDecision = autoEvaluateCondition(status.currentStep.condition, contact);
      if (autoDecision) {
        // Small delay so the user can see the card briefly before it auto-resolves
        const timer = setTimeout(() => {
          handleActionAndAdvance(contact.id, status.stepIndex, autoDecision);
        }, 800);
        return () => clearTimeout(timer);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, sortedContacts, completingStep]);

  // Close note input + collapse bio + email history + capture form when card changes
  useEffect(() => {
    setNoteOpen(false);
    setNoteText("");
    setNoteType("private");
    setEditingNote(null);
    setEditingText("");
    setEditingType("private");
    setBioExpanded(false);
    setEmailHistoryOpen(false);
    setEmailModalOpen(false);
    setCaptureData({});
    setNoteDraft(EMPTY_NOTE_DRAFT);
  }, [currentIndex]);

  // Load email history for the current contact (cached per contact ID)
  const loadEmailsForContact = useCallback(async (contact) => {
    if (!userId || !contact?.lead_email || contactEmails[contact.id]) return;

    const emailAddresses = contact.lead_email.split(',').map(e => e.trim().toLowerCase()).filter(Boolean);
    if (emailAddresses.length === 0) return;

    setLoadingEmails(prev => ({ ...prev, [contact.id]: true }));

    try {
      let allEmails = [];
      const seenIds = new Set();
      const sentRef = collection(firestore, `world_sent/${userId}/emails`);

      // By leadId
      try {
        const snap = await getDocs(query(sentRef, where("leadId", "==", contact.id)));
        snap.docs.forEach(d => {
          if (!seenIds.has(d.id)) {
            seenIds.add(d.id);
            allEmails.push({ id: d.id, ...d.data(), emailDirection: 'sent', timestamp: d.data().sentAt || d.data().createdAt || Date.now() });
          }
        });
      } catch (_) {}

      // By to-address(es)
      for (const addr of emailAddresses) {
        try {
          const snap = await getDocs(query(sentRef, where("to", "==", addr)));
          snap.docs.forEach(d => {
            if (!seenIds.has(d.id)) {
              seenIds.add(d.id);
              allEmails.push({ id: d.id, ...d.data(), emailDirection: 'sent', timestamp: d.data().sentAt || d.data().createdAt || Date.now() });
            }
          });
        } catch (_) {}
      }

      // Inbox — filter from field
      try {
        const inboxSnap = await getDocs(collection(firestore, `world_inbox/${userId}/emails`));
        inboxSnap.docs.forEach(d => {
          const from = (d.data().from || "").toLowerCase();
          if (emailAddresses.some(addr => from.includes(addr)) && !seenIds.has(d.id)) {
            seenIds.add(d.id);
            allEmails.push({ id: d.id, ...d.data(), emailDirection: 'received', timestamp: d.data().date ? new Date(d.data().date).getTime() : Date.now() });
          }
        });
      } catch (_) {}

      allEmails.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
      setContactEmails(prev => ({ ...prev, [contact.id]: allEmails }));
    } catch (err) {
      console.error("Error loading emails for contact:", err);
      setContactEmails(prev => ({ ...prev, [contact.id]: [] }));
    } finally {
      setLoadingEmails(prev => ({ ...prev, [contact.id]: false }));
    }
  }, [userId, contactEmails]);


  // Stable ref to the contact ID the note panel was opened for
  const noteContactIdRef = useRef(null);

  // Save note to lead_notes array
  const handleSaveNote = useCallback(async () => {
    if (!isWriteAllowed) { showToast('Read-only access', 'error'); return; }
    const targetId = noteContactIdRef.current;
    if (!noteText.trim() || !targetId || !userId) return;
    setNoteSaving(true);
    try {
      const contactRef = doc(firestore, `World/${userId}/db/${targetId}`);
      const newNote = {
        id: `note_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        text: noteText.trim(),
        type: noteType,
        createdAt: Date.now(),
        ...getNoteAuthor(),
      };
      await updateDoc(contactRef, {
        lead_notes: arrayUnion(newNote),
        updated_at: Date.now(),
      });
      showToast('✓ Note added', 'success');
      setNoteText("");
      setNoteType("private");
    } catch (error) {
      console.error("Error saving note:", error);
      showToast('Error saving note', 'error');
    }
    setNoteSaving(false);
  }, [noteText, noteType, userId, isWriteAllowed, showToast]);

  // Delete a single note from lead_notes
  const handleDeleteNote = useCallback(async (contact, noteToDelete) => {
    if (!isWriteAllowed) { showToast('Read-only access', 'error'); return; }
    if (!contact || !userId) return;
    try {
      const contactRef = doc(firestore, `World/${userId}/db/${contact.id}`);
      await updateDoc(contactRef, {
        lead_notes: arrayRemove(noteToDelete),
        updated_at: Date.now(),
      });
      showToast('Note removed', 'info');
    } catch (error) {
      console.error("Error deleting note:", error);
      showToast('Error removing note', 'error');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isWriteAllowed]);

  // Edit an existing note (remove old, add updated)
  const handleEditNote = useCallback(async (contact, oldNote, newText, newType) => {
    if (!isWriteAllowed) { showToast('Read-only access', 'error'); return; }
    if (!contact || !userId || !newText.trim()) return;
    try {
      const contactRef = doc(firestore, `World/${userId}/db/${contact.id}`);
      const updatedNote = { ...oldNote, text: newText.trim(), type: newType || oldNote.type || 'private', editedAt: Date.now() };
      await updateDoc(contactRef, { lead_notes: arrayRemove(oldNote) });
      await updateDoc(contactRef, { lead_notes: arrayUnion(updatedNote), updated_at: Date.now() });
      showToast('✓ Note updated', 'success');
      setEditingNote(null);
      setEditingText("");
      setEditingType("private");
    } catch (error) {
      console.error("Error editing note:", error);
      showToast('Error updating note', 'error');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isWriteAllowed]);

  // Toggle favorite tag on contact
  const isFavorite = useCallback((contact) => {
    return !!(contact?.tags && contact.tags[FAVORITE_TAG.id]);
  }, []);

  const handleToggleFavorite = useCallback(async (contact) => {
    if (!isWriteAllowed) { showToast('Read-only access', 'error'); return; }
    if (!contact || !userId) return;
    try {
      const contactRef = doc(firestore, `World/${userId}/db/${contact.id}`);
      const now = Date.now();
      if (isFavorite(contact)) {
        // Remove favorite tag
        await updateDoc(contactRef, {
          [`tags.${FAVORITE_TAG.id}`]: deleteField(),
          updated_at: now,
        });
        showFavToast('removed');
      } else {
        // Add favorite tag (same structure as flow tag nodes)
        await updateDoc(contactRef, {
          [`tags.${FAVORITE_TAG.id}`]: {
            id: FAVORITE_TAG.id,
            name: FAVORITE_TAG.name,
            color: FAVORITE_TAG.color,
            icon: FAVORITE_TAG.icon,
            addedAt: now,
            addedBy: 'leadswipe',
          },
          updated_at: now,
        });
        showFavToast('added');
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
      showToast('Error updating favorite', 'error');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isFavorite, isWriteAllowed]);

  // Open AI Email Popup for current contact
  const handleOpenEmailPopup = useCallback((contact) => {
    if (!contact || !contact.lead_email?.trim()) return;
    // Enrich with selectedTopic (same logic as NewWorldDbTable.handleEmailAiClick)
    let selectedTopic = null;
    const hasReplied = contact.lead_reply === true;
    if (hasReplied && contact.tags && typeof contact.tags === 'object') {
      const tagsArray = Object.values(contact.tags).sort((a, b) => new Date(b.appliedAt || 0) - new Date(a.appliedAt || 0));
      if (tagsArray.length > 0) {
        selectedTopic = tagsArray[0].selectedTopic;
      }
    }
    setEmailPopupRowData({ ...contact, selectedTopic });
    setEmailPopupOpen(true);
  }, []);

  // Auto-load emails when the current contact changes
  const currentContactForEmails = sortedContacts[Math.min(currentIndex, Math.max(0, sortedContacts.length - 1))];
  useEffect(() => {
    if (currentContactForEmails?.lead_email) {
      loadEmailsForContact(currentContactForEmails);
    }
  }, [currentContactForEmails?.id]);

  // Clamp index when contacts shrink (e.g. removed by onSnapshot)
  useEffect(() => {
    if (sortedContacts.length > 0 && currentIndex > sortedContacts.length - 1) {
      setCurrentIndex(sortedContacts.length - 1);
    }
  }, [currentIndex, sortedContacts.length]);

  const isLoading = teamLoading || !flowsLoaded || contactsLoading;
  // eslint-disable-next-line no-unused-vars
  const _tick = tick;
  const readyCount = contacts.filter(c => matchesPlatformFilter(c) && isEffectivelyReady(c)).length;

  if (isLoading) {
    return (
      <div className="ls">
        <div className="ls-loading"><div className="ls-loading-spinner" /></div>
      </div>
    );
  }

  const displayContacts = removedMode ? removedContacts : sortedContacts;
  const safeIndex = displayContacts.length > 0 ? Math.min(currentIndex, displayContacts.length - 1) : 0;
  const currentContact = displayContacts[safeIndex];
  const flowStatus = currentContact ? getFlowStatus(currentContact) : null;
  // Check if current flow step is an email step
  const isEmailStep = flowStatus && EMAIL_ACTION_TYPES.includes(flowStatus.currentStep?.type);

  // Email suppression: email steps are "waiting" unless the email platform filter is active
  const emailSuppressed = isEmailStep && platformFilter !== "email";

  // Effective ready: combines Firestore readiness with email suppression
  const effectiveReady = flowStatus?.ready && !emailSuppressed;

  const isAiSendActive = isEmailStep && effectiveReady && !!currentContact?.lead_email?.trim();

  // Get contextual action link for the current step
  const getStepActionLink = () => {
    if (!flowStatus || !currentContact) return null;
    const stepType = flowStatus.currentStep?.type || '';
    const socials = parseSocials(currentContact);
    if (stepType.startsWith('instagram_')) {
      const ig = socials.instagram;
      return ig ? buildSocialHref('instagram', ig) : null;
    }
    if (stepType.startsWith('linkedin_')) {
      const li = socials.linkedin;
      return li ? buildSocialHref('linkedin', li) : null;
    }
    if (stepType.startsWith('whatsapp_') && currentContact.lead_telephone?.trim()) {
      return `https://wa.me/${currentContact.lead_telephone.replace(/[^0-9]/g, '')}`;
    }
    return null;
  };
  const stepActionLink = getStepActionLink();
  const isConditionStep = flowStatus?.currentStep?.nodeType === 'condition';
  const isDelayStep = flowStatus?.currentStep?.nodeType === 'delay';
  const isAutoStep = flowStatus?.currentStep?.nodeType === 'auto_send_email' || flowStatus?.currentStep?.nodeType === 'auto_check_reply';

  // Social links helper – reads from lead_socials JSON + legacy fields
  const socialLinks = currentContact ? (() => {
    const socials = parseSocials(currentContact);
    const links = [];
    for (const platform of socialPlatforms) {
      const value = socials[platform.key];
      const href = buildSocialHref(platform.key, value);
      if (href) links.push({ href, icon: platform.icon, cls: platform.cls, label: platform.label });
    }
    if (currentContact.lead_telephone?.trim()) {
      links.push({ href: `https://wa.me/${currentContact.lead_telephone.replace(/[^0-9]/g, '')}`, icon: "fab fa-whatsapp", cls: "wa", label: "WhatsApp" });
    }
    if (currentContact.lead_link?.trim()) {
      const raw = currentContact.lead_link.trim();
      const href = raw.startsWith('http') ? raw : `https://${raw}`;
      links.push({ href, icon: "fa fa-link", cls: "link", label: "Link" });
    }
    return links;
  })() : [];

  return (
    <div className={`ls ${sidebarOpen ? 'ls--sidebar-open' : ''}`}>
      {/* Toast */}
      {toast && <div className={`ls-toast ls-toast--${toast.type}`}>{toast.message}</div>}

      {/* Favorite Toast – bottom right with flying heart */}
      {favToast && (
        <div key={favToast.key} className={`ls-fav-toast ls-fav-toast--${favToast.type}`}>
          <span className="ls-fav-toast__heart">
            <i className="fa fa-heart"></i>
          </span>
          <span className="ls-fav-toast__text">
            {favToast.type === 'added' ? 'Added to Favorites' : 'Removed from Favorites'}
          </span>
        </div>
      )}

      {/* Confirm Dialog for dangerous No-decisions */}
      {confirmDialog && (
        <div className="ls-confirm-overlay" onClick={() => setConfirmDialog(null)}>
          <div className="ls-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="ls-confirm__icon">
              <i className={`fa ${confirmDialog.isDisqualified ? 'fa-exclamation-triangle' : 'fa-info-circle'}`}></i>
            </div>
            <h3 className="ls-confirm__title">
              {confirmDialog.isDisqualified ? 'Remove from flow?' : 'End flow?'}
            </h3>
            <p className="ls-confirm__text">
              {confirmDialog.isDisqualified
                ? <><strong>{confirmDialog.contactName}</strong> will be marked as disqualified and removed from this flow. This cannot be undone from Swipe.</>
                : <><strong>{confirmDialog.contactName}</strong> will reach the end of this flow after this decision.</>
              }
            </p>
            <div className="ls-confirm__actions">
              <button className="ls-confirm__btn ls-confirm__btn--cancel" onClick={() => setConfirmDialog(null)}>
                Cancel
              </button>
              <button
                className={`ls-confirm__btn ${confirmDialog.isDisqualified ? 'ls-confirm__btn--danger' : 'ls-confirm__btn--confirm'}`}
                onClick={() => {
                  const { contactId, stepIndex } = confirmDialog;
                  setConfirmDialog(null);
                  handleActionAndAdvance(contactId, stepIndex, 'no');
                }}
              >
                {confirmDialog.isDisqualified ? 'Yes, remove' : 'Yes, continue'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="ls-header">
        <div className="ls-header__left">
          <h1 className="ls-header__title" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }}>
            <img 
              src="/img/salesgod_dark.webp" 
              alt="SalesGod" 
              className="ls-header__icon" 
            />
            SalesGod<span style={{fontWeight: 300, marginLeft: '-4px'}}>Swipe</span>
          </h1>
          {readyCount > 0 && <span className="ls-header__ready">{readyCount}</span>}
        </div>
        <div className="ls-header__right">
          {/* Platform path switcher – Instagram vs. LinkedIn (mutually exclusive) */}
          <div className="ls-platform-switch" role="group" aria-label="Platform path">
            <button
              type="button"
              className={`ls-platform-switch__btn ls-platform-switch__btn--ig ${platformFilter === "instagram" ? "ls-platform-switch__btn--active" : ""}`}
              onClick={() => setPlatformFilter("instagram")}
              title="Instagram path"
            >
              <i className="fab fa-instagram"></i>
              <span>Instagram</span>
            </button>
            <button
              type="button"
              className={`ls-platform-switch__btn ls-platform-switch__btn--li ${platformFilter === "linkedin" ? "ls-platform-switch__btn--active" : ""}`}
              onClick={() => setPlatformFilter("linkedin")}
              title="LinkedIn path"
            >
              <i className="fab fa-linkedin"></i>
              <span>LinkedIn</span>
            </button>
          </div>

          <div className="ls-header__filters">
            {[
              { key: "all", label: "All" },
              { key: "ready", label: "Ready", icon: "fa fa-circle" },
              { key: "favorites", label: "Favorites", icon: "fa fa-heart" },
              { key: "waiting", label: "Later", icon: "fa fa-clock" },
            ].map(f => (
              <button key={f.key} className={`ls-filter ${filterType === f.key ? 'ls-filter--active' : ''} ${f.key === 'favorites' ? 'ls-filter--fav' : ''}`} onClick={() => setFilterType(f.key)}>
                {f.icon && <i className={f.icon}></i>}
                {f.label}
              </button>
            ))}
          </div>

          <button className="ls-header__sidebar-toggle" onClick={() => setSidebarOpen(prev => !prev)} title="Flow Map">
            <i className="fa fa-sitemap"></i>
          </button>

          <button className="ls-header__dots" onClick={() => setMobileFiltersOpen(prev => !prev)}>
            <i className="fas fa-ellipsis-v"></i>
          </button>
        </div>

        {mobileFiltersOpen && (
          <>
            <div className="ls-filter-overlay" onClick={() => setMobileFiltersOpen(false)} />
            <div className="ls-filter-dropdown">
              <div className="ls-filter-dropdown__group">
                <span className="ls-filter-dropdown__label">Status</span>
                {[
                  { key: "all", label: "All", icon: null },
                  { key: "ready", label: "Ready", icon: "fa fa-circle" },
                  { key: "favorites", label: "Favorites", icon: "fa fa-heart" },
                  { key: "waiting", label: "Later", icon: "fa fa-clock" },
                ].map(f => (
                  <button key={f.key} className={`ls-filter-dropdown__item ${filterType === f.key ? 'ls-filter-dropdown__item--active' : ''}`} onClick={() => { setFilterType(f.key); setMobileFiltersOpen(false); }}>
                    {f.icon && <i className={f.icon}></i>}
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="ls-filter-dropdown__divider" />
              <div className="ls-filter-dropdown__group">
                <span className="ls-filter-dropdown__label">Platform</span>
                {[
                  { key: "instagram", label: "Instagram", icon: "fab fa-instagram" },
                  { key: "linkedin", label: "LinkedIn", icon: "fab fa-linkedin" },
                ].map(p => (
                  <button
                    key={p.key}
                    className={`ls-filter-dropdown__item ls-filter-dropdown__item--${p.key} ${platformFilter === p.key ? 'ls-filter-dropdown__item--active' : ''}`}
                    onClick={() => { setPlatformFilter(p.key); setMobileFiltersOpen(false); }}
                  >
                    <i className={p.icon}></i>
                    {p.label}
                  </button>
                ))}
              </div>
              {/* Mailing button – hidden for now
              <div className="ls-filter-dropdown__divider" />
              <div className="ls-filter-dropdown__group">
                <button
                  className={`ls-filter-dropdown__item ls-filter-dropdown__item--email ${platformFilter === "email" ? 'ls-filter-dropdown__item--active' : ''}`}
                  onClick={() => { setPlatformFilter(platformFilter === "email" ? "all" : "email"); setMobileFiltersOpen(false); }}
                >
                  <i className="fa fa-paper-plane"></i>
                  Mailing
                </button>
              </div>
              */}
            </div>
          </>
        )}
      </div>

      {/* Removed mode banner */}
      {removedMode && (
        <div className="ls-removed-banner">
          <button className="ls-removed-banner__back" onClick={exitRemovedMode}>
            <i className="fa fa-arrow-left"></i>
          </button>
          <i className="fa fa-user-slash ls-removed-banner__icon"></i>
          <span className="ls-removed-banner__label">
            Removed{removedLoading ? "" : ` (${removedContacts.length})`}
          </span>
        </div>
      )}

      {/* Empty */}
      {displayContacts.length === 0 && !removedLoading && (
        <div className="ls-empty">
          <div className="ls-empty__illustration">
            <div className="ls-empty__orb ls-empty__orb--1" />
            <div className="ls-empty__orb ls-empty__orb--2" />
            <div className="ls-empty__orb ls-empty__orb--3" />
            <div className="ls-empty__icon-wrap">
              {removedMode
                ? <i className="fa fa-user-slash"></i>
                : warmupFlows.length === 0
                  ? <i className="fa fa-sitemap"></i>
                  : filterType === "favorites"
                    ? <i className="fa fa-heart"></i>
                    : filterType === "waiting"
                      ? <i className="fa fa-clock"></i>
                      : <i className="fa fa-check-circle"></i>
              }
            </div>
          </div>
          <h2 className="ls-empty__title">
            {removedMode
              ? "No removed contacts"
              : warmupFlows.length === 0 ? "No flows yet" : filterType === "favorites" ? "No favorites yet" : filterType === "waiting" ? "Nothing waiting" : "All done!"}
          </h2>
          <p className="ls-empty__subtitle">
            {removedMode
              ? "No contacts have been removed from this flow yet."
              : warmupFlows.length === 0
                ? "Create a flow in World Settings to get started."
                : filterType === "favorites"
                  ? "Tap the heart icon on a contact card to add favorites."
                  : filterType === "waiting"
                    ? "No contacts are currently waiting on a delay step."
                    : filterType === "ready"
                      ? "You've worked through all ready contacts. Nice work."
                      : "No active contacts match this filter right now."}
          </p>
          {removedMode ? (
            <div className="ls-empty__actions">
              <button className="ls-empty__btn ls-empty__btn--primary" onClick={exitRemovedMode}>
                <i className="fa fa-arrow-left" style={{ marginRight: 6 }}></i> Back to Swipe
              </button>
            </div>
          ) : filterType !== "all" && (
            <div className="ls-empty__actions">
              <button className="ls-empty__btn ls-empty__btn--primary" onClick={() => setFilterType("all")}>
                Show all contacts
              </button>
            </div>
          )}
        </div>
      )}

      {/* Removed loading */}
      {removedMode && removedLoading && (
        <div className="ls-loading"><div className="ls-loading-spinner" /></div>
      )}

      {/* Removed-mode Card */}
      {removedMode && currentContact && !removedLoading && (
        <div className="ls-stage">
          <div className="ls-stage__center">
            <div className="ls-card-wrap">
              <div className="ls-card ls-card--waiting">
                <div className="ls-card__top">
                  <span className="ls-card__counter">{currentIndex + 1} / {displayContacts.length}</span>
                  <div className="ls-card__flow-info">
                    <span className="ls-card__flow-step" style={{ color: '#ef4444' }}>
                      {currentContact._endType === 'disqualified' || currentContact._endType === 'not_interested' || currentContact._endType === 'wrong_contact'
                        ? 'Disqualified'
                        : currentContact._endType === 'stopped'
                          ? 'Stopped'
                          : 'Completed'}
                    </span>
                  </div>
                  {currentContact._endedAt && (
                    <div className="ls-card__status ls-card__status--wait">
                      {new Date(currentContact._endedAt).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}
                    </div>
                  )}
                </div>
                <div className="ls-card__scroll">
                  <div className="ls-card__profile">
                    <div className="ls-card__avatar-wrap">
                      <div className={`ls-card__avatar ${currentContact.profile_image?.url ? 'ls-card__avatar--img' : ''}`}>
                        {currentContact.profile_image?.url
                          ? <img src={currentContact.profile_image.url} alt="" />
                          : <span>{getContactName(currentContact).charAt(0).toUpperCase()}</span>
                        }
                      </div>
                    </div>
                    <h2 className="ls-card__name">{getContactName(currentContact)}</h2>
                    {currentContact.lead_description && (
                      <p className="ls-card__bio ls-card__bio--expanded">{currentContact.lead_description}</p>
                    )}
                  </div>
                  {currentContact.lead_email && (
                    <div className="ls-card__meta">
                      <div className="ls-card__meta-item">
                        <i className="fa fa-envelope"></i>
                        <span>{currentContact.lead_email}</span>
                      </div>
                    </div>
                  )}
                  {currentContact.business_name && currentContact.lead_firstname && (
                    <div className="ls-card__meta">
                      <div className="ls-card__meta-item">
                        <i className="fa fa-building"></i>
                        <span>{currentContact.business_name}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="ls-actions">
            <div className="ls-round-wrap">
              <button className="ls-round ls-round--nav" onClick={goPrev} disabled={currentIndex <= 0} title="Prev">
                <i className="fa fa-chevron-left"></i>
              </button>
              <span className="ls-round-label">Prev</span>
            </div>
            <div className="ls-round-wrap">
              <button className="ls-round ls-round--nav" onClick={goNext} disabled={currentIndex >= displayContacts.length - 1} title="Next">
                <i className="fa fa-chevron-right"></i>
              </button>
              <span className="ls-round-label">Next</span>
            </div>
          </div>
        </div>
      )}

      {/* Card Stage */}
      {currentContact && flowStatus && !removedMode && (
        <div className="ls-stage">
          {/* Card – vertically centered */}
          {flowStatus && (
            <>
              <div className="ls-stage__center">
                <div 
                  className={`ls-card-wrap ${swipeDirection ? `ls-card-wrap--${swipeDirection}` : ''} ${isDragging ? 'ls-card-wrap--drag' : ''}`}
                  style={isDragging ? { transform: `translateX(${dragX}px) rotate(${dragX * 0.04}deg)`, opacity: 1 - Math.abs(dragX) / 500 } : {}}
                  ref={cardRef}
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onPointerLeave={handlePointerUp}
                >
                  <div className={`ls-card ${effectiveReady ? 'ls-card--ready' : 'ls-card--waiting'} ${isFavorite(currentContact) ? 'ls-card--fav' : ''}`}>
                    {/* Top bar */}
                    <div className="ls-card__top">
                      <span className="ls-card__counter">{currentIndex + 1} / {displayContacts.length}</span>
                      <div className="ls-card__flow-info">
                        <span className="ls-card__flow-name">{flowStatus.flow.name || 'Flow'}</span>
                        <span className="ls-card__flow-step">Step {flowStatus.stepIndex + 1} of {flowStatus.flow.steps.length}</span>
                      </div>
                      <div className={`ls-card__status ${effectiveReady ? 'ls-card__status--ready' : 'ls-card__status--wait'}`}>
                        {emailSuppressed ? 'Mail Queue' : effectiveReady ? 'Ready' : formatTimeRemaining(flowStatus.timeRemaining)}
                      </div>
                    </div>

                    {/* Scrollable content area */}
                    <div className="ls-card__scroll">
                    {/* Profile */}
                    <div className="ls-card__profile" onClick={() => currentContact.lead_description ? setBioExpanded(prev => !prev) : null} title={currentContact.lead_description ? "Tap to expand" : ""}>
                      <div className="ls-card__avatar-wrap">
                        <div className={`ls-card__avatar ${effectiveReady ? 'ls-card__avatar--ready' : ''} ${currentContact.profile_image?.url ? 'ls-card__avatar--img' : ''}`}>
                          {currentContact.profile_image?.url 
                            ? <img src={currentContact.profile_image.url} alt="" />
                            : <span>{getContactName(currentContact).charAt(0).toUpperCase()}</span>
                          }
                        </div>
                        <button
                          className={`ls-fav ${isFavorite(currentContact) ? 'ls-fav--active' : ''}`}
                          onClick={(e) => { e.stopPropagation(); handleToggleFavorite(currentContact); }}
                          title={isFavorite(currentContact) ? "Remove favorite" : "Add to favorites"}
                        >
                          <i className={`${isFavorite(currentContact) ? 'fa' : 'far'} fa-heart`}></i>
                        </button>
                      </div>
                      <h2 className="ls-card__name">{getContactName(currentContact)}</h2>
                      {currentContact.lead_description && (
                        <div className="ls-card__bio-wrap">
                          <p className={`ls-card__bio ${bioExpanded ? 'ls-card__bio--expanded' : ''}`}>
                            {currentContact.lead_description}
                          </p>
                          {currentContact.lead_description.length > 110 && (
                            <button className="ls-card__bio-toggle" onClick={(e) => { e.stopPropagation(); setBioExpanded(prev => !prev); }}>
                              {bioExpanded ? 'Show less' : 'Read more'} <i className={`fas fa-chevron-${bioExpanded ? 'up' : 'down'}`}></i>
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Mini Step Timeline */}
                    {flowStatus && flowStatus.flow.steps.length > 1 && (() => {
                      const timeline = buildTimelinePath(flowStatus.flow, currentContact.warmupFlow?.stepHistory, flowStatus.stepIndex);
                      if (timeline.length < 2) return null;
                      return (
                        <div className="ls-card__timeline">
                          {timeline.map((item, idx) => {
                            const tp = getTouchpointInfo(item.step.type, item.step);
                            const isCondition = item.step.nodeType === 'condition';
                            const isDelay = item.step.nodeType === 'delay';
                            const isEnd = item.step.nodeType === 'end';
                            const isAuto = item.step.nodeType === 'auto_send_email' || item.step.nodeType === 'auto_check_reply';
                            return (
                              <React.Fragment key={item.step.id || idx}>
                                <div className={`ls-tl ${item.status === 'done' ? 'ls-tl--done' : ''} ${item.status === 'current' ? 'ls-tl--current' : ''} ${item.status === 'upcoming' ? 'ls-tl--upcoming' : ''}`}
                                  title={`${item.status === 'done' ? '✓ ' : ''}${isCondition ? (item.step.customQuestion || 'Decision') : (tp.label || item.step.type)}${item.decision ? ` → ${item.decision}` : ''}`}
                                >
                                  <div className="ls-tl__dot">
                                    {item.status === 'done'
                                      ? <i className="fa fa-check"></i>
                                      : isCondition
                                        ? <i className="fa fa-question"></i>
                                        : isDelay
                                          ? <i className="fa fa-hourglass-half"></i>
                                          : isEnd
                                            ? <i className="fa fa-flag-checkered"></i>
                                            : isAuto
                                              ? <i className="fa fa-robot"></i>
                                              : <i className={`${tp.isBrand ? 'fab' : 'fa'} ${tp.icon}`}></i>
                                    }
                                  </div>
                                  {item.status === 'done' && isCondition && item.decision && (
                                    <span className={`ls-tl__decision ls-tl__decision--${item.decision}`}>
                                      {item.decision === 'yes' ? 'Y' : 'N'}
                                    </span>
                                  )}
                                </div>
                                {idx < timeline.length - 1 && (
                                  <div className={`ls-tl__line ${item.status === 'done' ? 'ls-tl__line--done' : ''}`} />
                                )}
                              </React.Fragment>
                            );
                          })}
                        </div>
                      );
                    })()}

                    {/* Social Links – icon circles only */}
                    {socialLinks.length > 0 && (
                      <div className="ls-card__social" onClick={(e) => e.stopPropagation()}>
                        {socialLinks.map((link, i) => (
                          <a key={i} href={link.href} target="_blank" rel="noopener noreferrer" className={`ls-social-circle ls-social-circle--${link.cls}`} onClick={(e) => e.stopPropagation()}>
                            <i className={link.icon}></i>
                          </a>
                        ))}
                      </div>
                    )}

                    {/* Add Note / Notes – full width centered */}
                    {!noteOpen && (
                      <div className="ls-card__addnote-row" onClick={(e) => e.stopPropagation()}>
                        <button className="ls-addnote-btn" onClick={() => { noteContactIdRef.current = currentContact?.id; setNoteOpen(true); setTimeout(() => noteInputRef.current?.focus(), 150); }}>
                          {currentContact.lead_notes?.length > 0
                            ? <><i className="fa fa-sticky-note"></i> {currentContact.lead_notes.length} Note{currentContact.lead_notes.length !== 1 ? 's' : ''}</>
                            : <>Add Note <i className="fa fa-plus"></i></>
                          }
                        </button>
                      </div>
                    )}

                    {/* Smart Step Action Button – only shown for non-email, non-condition, non-delay, non-auto steps */}
                    {flowStatus && !isConditionStep && !isDelayStep && !isEmailStep && !emailSuppressed && !isAutoStep && !flowStatus.isLogNotes && !flowStatus.isDataCapture && (
                      <div className="ls-card__step-action" onClick={(e) => e.stopPropagation()}>
                        {stepActionLink ? (
                          <a href={stepActionLink} target="_blank" rel="noopener noreferrer" className={`ls-step-btn ${effectiveReady ? 'ls-step-btn--active' : 'ls-step-btn--disabled'}`}>
                            <i className={`${flowStatus.touchpoint.isBrand ? 'fab' : 'fa'} ${flowStatus.touchpoint.icon}`}></i>
                            <span>{flowStatus.touchpoint.readyLabel || 'Open'}</span>
                          </a>
                        ) : (
                          <button className={`ls-step-btn ${effectiveReady ? 'ls-step-btn--active' : 'ls-step-btn--disabled'}`} disabled={!effectiveReady}>
                            <i className={`${flowStatus.touchpoint.isBrand ? 'fab' : 'fa'} ${flowStatus.touchpoint.icon}`}></i>
                            <span>{flowStatus.touchpoint.readyLabel || 'Complete Action'}</span>
                          </button>
                        )}
                      </div>
                    )}

                    {/* Notes Panel (expanded) */}
                    {noteOpen && (
                      <div className="ls-notes-panel" onClick={(e) => e.stopPropagation()}>
                        <div className="ls-notes-panel__header">
                          <span className="ls-notes-panel__title"><i className="fa fa-sticky-note"></i> Notes</span>
                          <button className="ls-notes-panel__close" onClick={() => { setNoteOpen(false); setNoteText(""); setNoteType("private"); }}>
                            <i className="fa fa-times"></i>
                          </button>
                        </div>

                        {currentContact.lead_notes?.length > 0 && (
                          <div className="ls-notes-panel__list">
                            {[...currentContact.lead_notes]
                              .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
                              .map((note) => (
                                <div key={note.id} className={`ls-notes-panel__item ${editingNote?.id === note.id ? 'ls-notes-panel__item--editing' : ''} ${note.type === 'outreach' ? 'ls-notes-panel__item--outreach' : ''}`}>
                                  {editingNote?.id === note.id ? (
                                    <div className="ls-notes-panel__edit">
                                      <div className="note-type-toggle note-type-toggle--sm">
                                        <button className={`note-type-toggle__btn ${editingType === 'private' ? 'note-type-toggle__btn--active' : ''}`} onClick={() => setEditingType('private')}><i className="fa fa-lock"></i> Private</button>
                                        <button className={`note-type-toggle__btn ${editingType === 'outreach' ? 'note-type-toggle__btn--active note-type-toggle__btn--outreach' : ''}`} onClick={() => setEditingType('outreach')}><i className="fa fa-bullhorn"></i> Outreach</button>
                                      </div>
                                      <textarea
                                        ref={editInputRef}
                                        className="ls-notes-panel__edit-input"
                                        value={editingText}
                                        onChange={(e) => setEditingText(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleEditNote(currentContact, note, editingText, editingType); } if (e.key === 'Escape') { setEditingNote(null); setEditingText(""); setEditingType("private"); } }}
                                        rows={2}
                                      />
                                      <div className="ls-notes-panel__edit-actions">
                                        <button className="ls-notes-panel__edit-cancel" onClick={() => { setEditingNote(null); setEditingText(""); setEditingType("private"); }}>Cancel</button>
                                        <button className="ls-notes-panel__edit-save" onClick={() => handleEditNote(currentContact, note, editingText, editingType)} disabled={!editingText.trim() || (editingText.trim() === note.text && editingType === (note.type || 'private'))}>
                                          <i className="fa fa-check"></i> Save
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <div className="ls-notes-panel__item-top">
                                        <span className={`note-type-badge ${note.type === 'outreach' ? 'note-type-badge--outreach' : 'note-type-badge--private'}`}>
                                          <i className={`fa ${note.type === 'outreach' ? 'fa-bullhorn' : 'fa-lock'}`}></i>
                                          {note.type === 'outreach' ? 'Outreach' : 'Private'}
                                        </span>
                                      </div>
                                      <p className="ls-notes-panel__text">{note.text}</p>
                                      <div className="ls-notes-panel__meta">
                                        <span className="ls-notes-panel__date">
                                          {new Date(note.editedAt || note.createdAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                          {note.editedAt ? ' (edited)' : ''}
                                        </span>
                                        <div className="ls-notes-panel__item-actions">
                                          <button className="ls-notes-panel__edit-btn" onClick={() => { setEditingNote(note); setEditingText(note.text); setEditingType(note.type || 'private'); setTimeout(() => editInputRef.current?.focus(), 50); }} title="Edit note">
                                            <i className="far fa-edit"></i>
                                          </button>
                                          <button className="ls-notes-panel__delete" onClick={() => handleDeleteNote(currentContact, note)} title="Delete note">
                                            <i className="far fa-trash-alt"></i>
                                          </button>
                                        </div>
                                      </div>
                                      {(note.source === 'flow' || note.createdByEmail) && (
                                        <div className="ls-notes-panel__prov">
                                          {note.source === 'flow' && (
                                            <span className="ls-notes-panel__prov-flow" title={note.flowName ? `Added via flow: ${note.flowName}` : 'Added via flow'}>
                                              <i className="fa fa-diagram-project"></i> {note.flowName || 'Flow'}
                                            </span>
                                          )}
                                          {note.createdByEmail && (
                                            <span className="ls-notes-panel__prov-author" title={`Saved by ${note.createdByEmail}`}>
                                              <i className="fa fa-user"></i> {note.createdByEmail}
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              ))
                            }
                          </div>
                        )}

                        <div className="ls-notes-panel__form">
                          <div className="note-type-toggle">
                            <button className={`note-type-toggle__btn ${noteType === 'private' ? 'note-type-toggle__btn--active' : ''}`} onClick={() => setNoteType('private')}><i className="fa fa-lock"></i> Private</button>
                            <button className={`note-type-toggle__btn ${noteType === 'outreach' ? 'note-type-toggle__btn--active note-type-toggle__btn--outreach' : ''}`} onClick={() => setNoteType('outreach')}><i className="fa fa-bullhorn"></i> Outreach</button>
                          </div>
                          <textarea
                            ref={noteInputRef}
                            className="ls-notes-panel__input"
                            placeholder={noteType === 'outreach' ? "Outreach info for AI emails…" : "Private note…"}
                            value={noteText}
                            onChange={(e) => setNoteText(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveNote(); } if (e.key === 'Escape') { setNoteOpen(false); setNoteText(""); setNoteType("private"); } }}
                            rows={2}
                            disabled={noteSaving}
                          />
                          <div className="ls-notes-panel__actions">
                            <button className="ls-notes-panel__save" onClick={handleSaveNote} disabled={!noteText.trim() || noteSaving}>
                              {noteSaving ? <i className="fa fa-spinner fa-spin"></i> : <><i className="fa fa-plus"></i> Add</>}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Email History – only visible if emails exist for this contact */}
                    {(() => {
                      const emails = contactEmails[currentContact.id];
                      const isLoading = loadingEmails[currentContact.id];
                      if (!emails && !isLoading) return null;
                      if (isLoading && !emails) return null;
                      if (emails && emails.length === 0) return null;

                      return (
                        <div className="ls-email-history" onClick={(e) => e.stopPropagation()}>
                          <button className="ls-email-history__toggle" onClick={() => setEmailModalOpen(true)}>
                            <div className="ls-email-history__toggle-left">
                              <i className="fa fa-envelope"></i>
                              <span>Email History</span>
                              <span className="ls-email-history__count">{emails.length}</span>
                            </div>
                            <i className="fa fa-expand"></i>
                          </button>
                        </div>
                      );
                    })()}

                    </div>{/* end ls-card__scroll */}

                    {/* Action Hero – hidden when note is open */}
                    {!noteOpen && <div className={`ls-card__action ${effectiveReady ? 'ls-card__action--ready' : 'ls-card__action--wait'}`}>
                      <div className="ls-card__action-container">
                        {/* Context badge for returning contacts */}
                        {(() => {
                          const hist = currentContact.warmupFlow?.stepHistory || {};
                          const completedEntries = Object.entries(hist)
                            .map(([idx, data]) => ({ stepIndex: Number(idx), ...data }))
                            .sort((a, b) => (b.completedAt || 0) - (a.completedAt || 0));
                          const lastCompleted = completedEntries[0];
                          const completedCount = completedEntries.length;
                          if (lastCompleted) {
                            const prevStep = flowStatus.flow.steps[lastCompleted.stepIndex];
                            if (prevStep?.nodeType === 'delay') {
                              const days = parseInt(prevStep.delayDays) || 0;
                              return (
                                <div className="ls-card__context-badge">
                                  <i className="fa fa-hourglass-half"></i>
                                  Back after {days}d wait
                                </div>
                              );
                            }
                            if (prevStep?.nodeType === 'condition' && lastCompleted.decision) {
                              return (
                                <div className="ls-card__context-badge">
                                  <i className="fa fa-code-branch"></i>
                                  Previous: {lastCompleted.decision === 'yes' ? 'Yes' : 'No'}
                                </div>
                              );
                            }
                          }
                          if (completedCount > 0 && !flowStatus.isManualCondition) {
                            return (
                              <div className="ls-card__context-badge">
                                <i className="fa fa-shoe-prints"></i>
                                {completedCount} step{completedCount !== 1 ? 's' : ''} completed
                              </div>
                            );
                          }
                          return null;
                        })()}
                        {flowStatus.isDataCapture ? (
                          <div className="ls-card__capture-form" onClick={(e) => e.stopPropagation()}>
                            <div className="ls-card__capture-title">
                              <i className="fa fa-database" style={{ color: '#7c3aed' }}></i>
                              <span>Capture Lead Data</span>
                            </div>
                            {(flowStatus.currentStep.captureFields || ['industry', 'size']).map(field => (
                              <div key={field} className="ls-card__capture-field">
                                {field === 'industry' && (
                                  <>
                                    <label>Industry</label>
                                    <select
                                      value={captureData.industry || ''}
                                      onChange={(e) => setCaptureData(prev => ({ ...prev, industry: e.target.value }))}
                                    >
                                      <option value="">Select...</option>
                                      {INDUSTRY_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                    </select>
                                  </>
                                )}
                                {field === 'size' && (
                                  <>
                                    <label>Company Size</label>
                                    <select
                                      value={captureData.size || ''}
                                      onChange={(e) => setCaptureData(prev => ({ ...prev, size: e.target.value }))}
                                    >
                                      <option value="">Select...</option>
                                      {SIZE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                    </select>
                                  </>
                                )}
                                {field === 'deal_value' && (
                                  <>
                                    <label>Deal Value (€)</label>
                                    <input
                                      type="number"
                                      placeholder="0"
                                      value={captureData.dealValue || ''}
                                      onChange={(e) => setCaptureData(prev => ({ ...prev, dealValue: e.target.value ? Number(e.target.value) : null }))}
                                    />
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : flowStatus.isLogNotes ? (
                          <div className="ls-card__capture-form ls-card__lognotes-form" onClick={(e) => e.stopPropagation()}>
                            <div className="ls-card__capture-title">
                              <i className="fa fa-clipboard-list" style={{ color: '#0ea5e9' }}></i>
                              <span>Log Notes</span>
                            </div>
                            {flowStatus.currentStep.note && (
                              <p className="ls-card__lognotes-hint">{flowStatus.currentStep.note}</p>
                            )}
                            <NotesActivityFields
                              compact
                              activity={noteDraft.activity}
                              setActivity={(v) => setNoteDraft(prev => ({ ...prev, activity: v }))}
                              outcome={noteDraft.outcome}
                              setOutcome={(v) => setNoteDraft(prev => ({ ...prev, outcome: v }))}
                              sentiment={noteDraft.sentiment}
                              setSentiment={(v) => setNoteDraft(prev => ({ ...prev, sentiment: v }))}
                              text={noteDraft.text}
                              setText={(v) => setNoteDraft(prev => ({ ...prev, text: v }))}
                              excludeFromAi={noteDraft.excludeFromAi}
                              setExcludeFromAi={(v) => setNoteDraft(prev => ({ ...prev, excludeFromAi: v }))}
                              rows={3}
                            />
                          </div>
                        ) : (
                        <div className="ls-card__action-main">
                          <div className={`ls-card__action-icon ${effectiveReady ? 'ls-card__action-icon--ready' : 'ls-card__action-icon--wait'}`}>
                            {emailSuppressed
                              ? <i className="fa fa-envelope"></i>
                              : <i className={`${flowStatus.isManualCondition ? 'fa fa-question' : (flowStatus.touchpoint.isBrand ? 'fab' : 'fa')} ${flowStatus.isManualCondition ? '' : flowStatus.touchpoint.icon}`}></i>
                            }
                          </div>
                          <div className="ls-card__action-label">
                            {emailSuppressed
                              ? "Waiting for mail to be sent"
                              : flowStatus.isManualCondition ? flowStatus.conditionLabel : (
                                !flowStatus.ready && flowStatus.currentStep?.nodeType === 'delay' && flowStatus.timeRemaining > 0
                                  ? (
                                    <div className="ls-card__action-wait">
                                      <div className="ls-card__action-countdown-grid">
                                        {formatTimeRemainingDetailed(flowStatus.timeRemaining)?.map((part, idx) => (
                                          <div key={idx} className="ls-card__action-countdown-item">
                                            <span className="ls-card__action-countdown-value">{part.value}</span>
                                            <span className="ls-card__action-countdown-label">{part.label}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )
                                  : (flowStatus.touchpoint.readyLabel || flowStatus.touchpoint.label)
                              )
                            }
                          </div>
                        </div>
                        )}
                        {flowStatus.currentStep.note && !flowStatus.isDataCapture && !flowStatus.isLogNotes && (
                          <div className="ls-card__action-note">"{flowStatus.currentStep.note}"</div>
                        )}
                      </div>
                    </div>}
                  </div>
                </div>

              </div>

              {/* Bottom – Tinder-style round buttons with labels */}
              <div className="ls-bottom">
                <div className="ls-bottom__actions">
                  {/* Prev */}
                  <div className="ls-round-wrap">
                    <button className="ls-round ls-round--nav" onClick={goPrev} disabled={currentIndex === 0} title="Previous">
                      <i className="fa fa-chevron-left"></i>
                    </button>
                    <span className="ls-round-label">Prev</span>
                  </div>

                  {isAutoStep ? (
                    <>
                      <div className="ls-round-wrap">
                        <button className="ls-round ls-round--no" onClick={goNext} disabled={currentIndex >= displayContacts.length - 1} title="Skip">
                          <i className="fa fa-times"></i>
                        </button>
                        <span className="ls-round-label">Skip</span>
                      </div>
                      <div className="ls-round-wrap">
                        <button className="ls-round ls-round--done ls-round--auto" disabled title="Automated step — handled by the system">
                          <i className="fa fa-robot"></i>
                        </button>
                        <span className="ls-round-label">Auto</span>
                      </div>
                    </>
                  ) : flowStatus.isDataCapture ? (
                    <>
                      <div className="ls-round-wrap">
                        <button className="ls-round ls-round--no" onClick={goNext} disabled={currentIndex >= displayContacts.length - 1} title="Skip">
                          <i className="fa fa-times"></i>
                        </button>
                        <span className="ls-round-label">Skip</span>
                      </div>
                      <div className="ls-round-wrap">
                        <button
                          className="ls-round ls-round--done"
                          style={{ backgroundColor: '#7c3aed' }}
                          disabled={!isWriteAllowed || !!completingStep[currentContact.id] || (!captureData.industry && !captureData.size && !captureData.dealValue)}
                          title="Save & Continue"
                          onClick={async () => {
                            if (!isWriteAllowed) return;
                            setCompletingStep(prev => ({ ...prev, [currentContact.id]: 'action' }));
                            try {
                              const contactRef = doc(firestore, `World/${userId}/db/${currentContact.id}`);
                              const crmUpdates = { updated_at: Date.now() };
                              if (captureData.industry) crmUpdates.lead_industry = captureData.industry;
                              if (captureData.size) crmUpdates.lead_size = captureData.size;
                              if (captureData.dealValue) crmUpdates.lead_deal_value = captureData.dealValue;
                              await updateDoc(contactRef, crmUpdates);
                              try { updateLeadContext(userId, currentContact.id, captureData); } catch (_) {}
                              if (captureData.industry) {
                                const oldInd = currentContact.leadSignals?.industry || 'unknown';
                                if (captureData.industry !== oldInd) {
                                  recordSignalCorrection(userId, currentContact.id, 'industry', oldInd, captureData.industry, currentContact).catch(() => {});
                                }
                              }
                              if (captureData.size) {
                                const sizeMap = { 'solo': 'SOLO', '2-10': 'SMALL', '11-50': 'SMALL', '51-200': 'MID', '201-1000': 'ENT', '1000+': 'ENT' };
                                const mappedSize = sizeMap[captureData.size] || captureData.size;
                                const oldSize = currentContact.leadSignals?.sizeProxy || 'unknown';
                                if (mappedSize !== oldSize) {
                                  recordSignalCorrection(userId, currentContact.id, 'sizeProxy', oldSize, mappedSize, currentContact).catch(() => {});
                                }
                              }
                              await handleActionAndAdvance(currentContact.id, flowStatus.stepIndex);
                            } catch (err) {
                              showToast('Error saving data', 'error');
                            }
                            setCompletingStep(prev => ({ ...prev, [currentContact.id]: null }));
                            setCaptureData({});
                          }}
                        >
                          {completingStep[currentContact.id] === 'action' ? <i className="fa fa-spinner fa-spin"></i> : <i className="fa fa-database"></i>}
                        </button>
                        <span className="ls-round-label">Save</span>
                      </div>
                    </>
                  ) : flowStatus.isLogNotes ? (
                    <>
                      <div className="ls-round-wrap">
                        <button className="ls-round ls-round--no" onClick={goNext} disabled={currentIndex >= displayContacts.length - 1} title="Skip">
                          <i className="fa fa-times"></i>
                        </button>
                        <span className="ls-round-label">Skip</span>
                      </div>
                      <div className="ls-round-wrap">
                        <button
                          className="ls-round ls-round--done"
                          style={{ backgroundColor: '#0ea5e9' }}
                          disabled={!isWriteAllowed || !!completingStep[currentContact.id] || !hasNoteContent(noteDraft)}
                          title="Save note & continue"
                          onClick={async () => {
                            if (!isWriteAllowed) return;
                            setCompletingStep(prev => ({ ...prev, [currentContact.id]: 'action' }));
                            try {
                              const note = createPrivateNote(noteDraft, {
                                source: 'flow',
                                flowId: currentContact.warmupFlow?.flowId || flowStatus.flow?.id || null,
                                flowName: currentContact.warmupFlow?.flowName || flowStatus.flow?.name || null,
                              });
                              const contactRef = doc(firestore, `World/${userId}/db/${currentContact.id}`);
                              await updateDoc(contactRef, { lead_notes: arrayUnion(note), updated_at: Date.now() });
                              await handleActionAndAdvance(currentContact.id, flowStatus.stepIndex);
                            } catch (err) {
                              showToast('Error saving note', 'error');
                            }
                            setCompletingStep(prev => ({ ...prev, [currentContact.id]: null }));
                            setNoteDraft(EMPTY_NOTE_DRAFT);
                          }}
                        >
                          {completingStep[currentContact.id] === 'action' ? <i className="fa fa-spinner fa-spin"></i> : <i className="fa fa-clipboard-check"></i>}
                        </button>
                        <span className="ls-round-label">Save</span>
                      </div>
                    </>
                  ) : flowStatus.isManualCondition ? (
                    <>
                      <div className="ls-round-wrap">
                        <button className="ls-round ls-round--no" onClick={() => effectiveReady && isWriteAllowed && handleSafeNoDecision(currentContact.id, flowStatus.stepIndex)} disabled={!effectiveReady || !isWriteAllowed || !!completingStep[currentContact.id]} title={!isWriteAllowed ? "Read-only" : effectiveReady ? "No" : "Waiting..."}>
                          {completingStep[currentContact.id] === 'no' ? <i className="fa fa-spinner fa-spin"></i> : <i className="fa fa-times"></i>}
                        </button>
                        <span className="ls-round-label">No</span>
                      </div>
                      <div className="ls-round-wrap">
                        <button className="ls-round ls-round--yes" onClick={() => effectiveReady && isWriteAllowed && handleActionAndAdvance(currentContact.id, flowStatus.stepIndex, 'yes')} disabled={!effectiveReady || !isWriteAllowed || !!completingStep[currentContact.id]} title={!isWriteAllowed ? "Read-only" : effectiveReady ? "Yes" : "Waiting..."}>
                          {completingStep[currentContact.id] === 'yes' ? <i className="fa fa-spinner fa-spin"></i> : <i className="fa fa-check"></i>}
                        </button>
                        <span className="ls-round-label">Yes</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="ls-round-wrap">
                        <button className="ls-round ls-round--no" onClick={goNext} disabled={currentIndex >= displayContacts.length - 1} title="Skip">
                          <i className="fa fa-times"></i>
                        </button>
                        <span className="ls-round-label">Skip</span>
                      </div>
                      <div className="ls-round-wrap">
                        <button className="ls-round ls-round--done" onClick={() => effectiveReady && isWriteAllowed && handleActionAndAdvance(currentContact.id, flowStatus.stepIndex)} disabled={!effectiveReady || !isWriteAllowed || !!completingStep[currentContact.id]} title={!isWriteAllowed ? "Read-only" : effectiveReady ? "Done" : "Waiting..."}>
                          {completingStep[currentContact.id] === 'action' ? <i className="fa fa-spinner fa-spin"></i> : <i className="fa fa-check"></i>}
                        </button>
                        <span className="ls-round-label">Done</span>
                      </div>
                    </>
                  )}

                  {/* Next */}
                  <div className="ls-round-wrap">
                    <button className="ls-round ls-round--nav" onClick={goNext} disabled={currentIndex >= displayContacts.length - 1} title="Next">
                      <i className="fa fa-chevron-right"></i>
                    </button>
                    <span className="ls-round-label">Next</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
      {/* Email History Modal – full thread view */}
      {emailModalOpen && currentContact && (() => {
        const emails = contactEmails[currentContact.id] || [];
        return (
          <div className="ls-email-modal-overlay" onClick={() => setEmailModalOpen(false)}>
            <div className="ls-email-modal" onClick={(e) => e.stopPropagation()}>
              <div className="ls-email-modal__header">
                <div className="ls-email-modal__title">
                  <i className="fa fa-envelope"></i>
                  <h2>Email History</h2>
                  <span className="ls-email-modal__count">{emails.length}</span>
                </div>
                <button className="ls-email-modal__close" onClick={() => setEmailModalOpen(false)}>
                  <i className="fa fa-times"></i>
                </button>
              </div>

              <div className="ls-email-modal__list">
                {emails.length > 0 ? emails.map((email) => {
                  const possibleHtml = email.bodyHtml || email.html || email.content || email.text || email.bodyText || "";
                  const plainText = email.preview || email.text || email.bodyText || email.content || "";
                  const isHtml = possibleHtml.includes('<div') || possibleHtml.includes('<p') || possibleHtml.includes('<table') || possibleHtml.includes('<br') || possibleHtml.includes('style=');

                  return (
                    <div key={email.id} className={`ls-email-modal__item ls-email-modal__item--${email.emailDirection}`}>
                      <div className="ls-email-modal__item-top">
                        <div className={`ls-email-modal__direction ls-email-modal__direction--${email.emailDirection}`}>
                          <i className={`fa ${email.emailDirection === 'sent' ? 'fa-paper-plane' : 'fa-inbox'}`}></i>
                          <span>{email.emailDirection === 'sent' ? 'Sent' : 'Received'}</span>
                        </div>
                        <span className="ls-email-modal__date">
                          {email.timestamp ? new Date(email.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                      </div>
                      <div className="ls-email-modal__item-info">
                        <span className="ls-email-modal__subject">{email.subject || "(No Subject)"}</span>
                        <span className="ls-email-modal__meta">
                          {email.emailDirection === 'sent' ? 'To: ' : 'From: '}
                          {email.emailDirection === 'sent' ? (email.to || email.recipients) : email.from}
                        </span>
                      </div>
                      <div className="ls-email-modal__body">
                        {email.htmlStorageUrl ? (
                          <iframe
                            src={email.htmlStorageUrl}
                            className="ls-email-modal__iframe"
                            title="Email Content"
                            sandbox="allow-same-origin allow-popups"
                          />
                        ) : (
                          <div className="ls-email-modal__letter">
                            <Letter
                              html={isHtml ? possibleHtml : ""}
                              text={isHtml ? "" : plainText}
                              useIframe={false}
                              allowedSchemas={['http', 'https', 'mailto']}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }) : (
                  <div className="ls-email-modal__empty">
                    <i className="fa fa-inbox"></i>
                    <span>No emails found</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Flow Sidebar – DAG visualization with step filtering */}
      <FlowSidebar
        warmupFlows={warmupFlows}
        contacts={contacts}
        currentContact={currentContact}
        selectedStepFilter={sidebarStepFilter}
        onStepFilterChange={setSidebarStepFilter}
        onRemovedClick={handleShowRemoved}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Email AI Popup – reuses the same EmailPopup from WorldDb */}
      <EmailPopup
        isPopupOpen={emailPopupOpen}
        rowData={emailPopupRowData}
        userId={userId}
        onEmailResult={(status, message) => {
          if (status === 'success') showToast('✓ Email sent', 'success');
          else if (status === 'error') showToast(message || 'Email error', 'error');
          else if (status === 'safeguard_rejected') showToast('Draft saved for review', 'info');
        }}
        closePopup={() => { setEmailPopupOpen(false); setEmailPopupRowData(null); }}
      />
    </div>
  );
};

export default LeadSwipe;
